import type { BookingRulesView, CancelReasonView } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useEffect, useId, useMemo, useRef, useState } from "react"

import { cx } from "../lib/cx"
import { Button } from "../ui/button"
import { Chip, Chips } from "../ui/chip"
import { Checkbox, SelectField, Switch, TextField } from "../ui/field"
import { cancelConsequence, noShowConsequence } from "./consequences"
import styles from "./journal-actions.module.css"
import "./journal-tokens.css"
import { groupWindows, moreLabel } from "./reschedule-windows"
import { formatMinute } from "./journal-time"

/**
 * Диалоги журнала: отмена (2-2.9.2), неявка (2-2.9.3), конфликт версий (2-2.10.3)
 * и перенос (§2.8.2).
 *
 * ПОЧЕМУ ЭТО МОДАЛЬНЫЕ ОКНА, А НЕ ОТДЕЛЬНЫЕ АДРЕСА-ЭКРАНЫ. Правило ТЗ §4 запрещает
 * прятать в модальное окно ДЛИННЫЙ сценарий — тот, из которого выходят с результатом
 * и к которому возвращаются по ссылке. Эти четыре длинными не являются: они занимают
 * один экран, требуют одного решения и заканчиваются за полминуты. Но АДРЕС у них
 * всё равно есть: диалог открыт — в строке запроса стоит ?dialog=cancel, и ссылку
 * на «эту запись, диалог отмены» можно переслать в поддержку. Адрес и модальность —
 * разные вещи, и правило проекта требует первого, а не запрещает второе.
 *
 * ОБЩЕЕ ДЛЯ ВСЕХ ЧЕТЫРЁХ: последствие названо ДО кнопки, а не после нажатия.
 */

/** Запись в том виде, в котором её показывают диалоги. */
export interface AppointmentBrief {
  readonly id: string
  readonly clientName: string
  /** «Ср, 16 сентября, 16:30» — день и время визита. */
  readonly whenLabel: string
  readonly employeeName: string
  /** «2 500,00 ₽, предоплата 500,00 ₽» либо null, когда деньги не показываются. */
  readonly moneyLabel: string | null
  readonly prepaidMinor: number | null
  readonly noShowCount: number
  /** Минут до визита. Отрицательное — визит уже прошёл. */
  readonly minutesUntilVisit: number
  /**
   * Есть ли у визита исполнитель. Неразобранная запись (мастер не назначен) занятости
   * в календаре не имеет, поэтому и карантина после отмены не будет — сервер его
   * не ставит, и диалог не должен обещать возврат времени, которого никто не держал.
   */
  readonly hasExecutor: boolean
}

/**
 * Оболочка модального окна.
 *
 * В библиотеке элементов модального окна нет (этап 0 его не заводил), а заводить его
 * сейчас в общей ui/ значит столкнуться с параллельными агентами в одном файле.
 * Поэтому окно живёт здесь и переезжает в библиотеку на приёмке П0 — вместе с тем,
 * что в нём уже отлажено: ловушка фокуса, Escape и возврат фокуса на место.
 */
export function JournalDialog(props: {
  readonly title: string
  readonly onClose: () => void
  readonly children: ReactNode
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const { onClose } = props

  useEffect(() => {
    const node = ref.current
    if (node === null) return
    const previous = document.activeElement

    const focusable = (): HTMLElement[] =>
      Array.from(
        node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )

    // Фокус переносится внутрь окна: иначе Tab уходит на кнопки под подложкой,
    // и человек нажимает «Отменить визит» вслепую, думая, что закрывает окно.
    const first = focusable()[0]
    if (first === undefined) node.focus()
    else first.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        // Escape не уходит выше: он закрывает верхний слой, а не всё сразу.
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== "Tab") return
      const list = focusable()
      const firstEl = list[0]
      const lastEl = list[list.length - 1]
      if (firstEl === undefined || lastEl === undefined) return
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault()
        lastEl.focus()
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener("keydown", onKeyDown, true)
    return () => {
      document.removeEventListener("keydown", onKeyDown, true)
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [onClose])

  return (
    <div
      className={styles["scrim"]}
      onPointerDown={(event) => {
        // Нажатие мимо окна закрывает его — но только само нажатие мимо, а не клик,
        // начавшийся внутри и закончившийся снаружи (выделение текста мышью).
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={ref}
        className={styles["dialog"]}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 className={styles["dialogTitle"]} id={titleId}>
          {props.title}
        </h2>
        {props.children}
      </div>
    </div>
  )
}

export interface CancelSubmit {
  readonly initiatedBy: "client" | "staff"
  readonly reasonCode: string
  readonly comment: string
  readonly notifyClient: boolean
}

/**
 * Отмена визита (2-2.9.2).
 *
 * ГЛАВНОЕ РЕШЕНИЕ ЭКРАНА: у поля «кто отменяет» НЕТ значения по умолчанию. От инициатора
 * зависят счётчик клиента и судьба предоплаты, и подставленный за администратора «салон»
 * означал бы, что салон каждый раз добровольно возвращает деньги; подставленный «клиент» —
 * что человеку каждый раз засчитывают отмену. Поэтому пока инициатор не выбран,
 * список причин состоит из одной строки-объяснения, а кнопка отмены выключена.
 */
export function CancelAppointmentDialog(props: {
  readonly appointment: AppointmentBrief
  /** «Клиент мог отменить сам до 13:30…» либо null, если срок ещё не прошёл. */
  readonly lateNote?: string | null
  readonly reasons: readonly CancelReasonView[]
  readonly rules: BookingRulesView
  readonly submitting: boolean
  readonly failure: string | null
  readonly onKeep: () => void
  readonly onSubmit: (value: CancelSubmit) => void
}): ReactNode {
  const [initiatedBy, setInitiatedBy] = useState<"client" | "staff" | null>(null)
  const [reasonCode, setReasonCode] = useState("")
  const [comment, setComment] = useState("")
  const [notifyClient, setNotifyClient] = useState(true)

  const reasons = useMemo(
    () =>
      initiatedBy === null
        ? []
        : props.reasons.filter((reason) => reason.actor === initiatedBy && reason.isActive),
    [props.reasons, initiatedBy],
  )

  const reason = reasons.find((item) => item.code === reasonCode) ?? null

  const consequence = cancelConsequence({
    initiatedBy,
    reason,
    rules: props.rules,
    prepaidMinor: props.appointment.prepaidMinor,
    clientNoShowCount: props.appointment.noShowCount,
    minutesUntilVisit: props.appointment.minutesUntilVisit,
    hasExecutor: props.appointment.hasExecutor,
    notifyClient,
  })

  const ready = initiatedBy !== null && reasonCode !== ""

  return (
    /*
     * В макете заголовок стоит в родительном падеже («Отменить визит Морозова Яна?»).
     * Кабинет так написать не может: API отдаёт одно поле displayName в именительном,
     * а склонять фамилии кодом — это «Морозова Яна» вперемешку с «Дюма Александра».
     * Поэтому имя вынесено в строку под заголовком, где именительный падеж уместен.
     */
    <JournalDialog title="Отменить визит?" onClose={props.onKeep}>
      <div className={styles["dialogSub"]}>
        {props.appointment.clientName} · {props.appointment.whenLabel} ·{" "}
        {props.appointment.employeeName}
        {props.appointment.moneyLabel === null ? "" : ` · ${props.appointment.moneyLabel}`}
      </div>

      {/* Почему клиент не отменил сам: срок салона прошёл, и теперь может только стойка.
          Разговор иначе начинается с «у вас не работает» (5-А3). */}
      {props.lateNote == null ? null : (
        <div className={styles["dialogNote"]}>{props.lateNote}</div>
      )}

      <div className={styles["field"]}>
        <span className={styles["fieldLabel"]}>
          Кто отменяет <span className={styles["required"]}>*</span>
        </span>
        <Chips label="Кто отменяет">
          <Chip
            active={initiatedBy === "client"}
            onClick={() => {
              setInitiatedBy("client")
              setReasonCode("")
            }}
          >
            Клиент
          </Chip>
          <Chip
            active={initiatedBy === "staff"}
            onClick={() => {
              setInitiatedBy("staff")
              setReasonCode("")
            }}
          >
            Салон
          </Chip>
        </Chips>
        <span className={styles["hint"]}>
          Значения по умолчанию нет: от инициатора зависят счётчик неявок и предоплата
        </span>
      </div>

      {initiatedBy !== null && reasons.length === 0 ? (
        <div className={styles["hint"]}>
          Справочник причин не загрузился. Обновите страницу — без причины отменить визит
          нельзя: от неё зависят счётчик клиента и судьба предоплаты.
        </div>
      ) : null}

      <SelectField
        label="Причина"
        required
        value={reasonCode}
        onChange={(event) => {
          setReasonCode(event.currentTarget.value)
        }}
        disabled={initiatedBy === null}
        options={
          initiatedBy === null
            ? [{ value: "", label: "Сначала выберите, кто отменяет" }]
            : [
                { value: "", label: "Выберите причину" },
                ...reasons.map((item) => ({ value: item.code, label: item.title })),
              ]
        }
      />

      <TextField
        label="Комментарий"
        placeholder="Необязательно"
        value={comment}
        onChange={(event) => {
          setComment(event.currentTarget.value)
        }}
      />

      <Switch checked={notifyClient} onChange={setNotifyClient}>
        Уведомить клиента об отмене
      </Switch>

      {/*
       * Последствие стоит ДО кнопки и меняется вместе с выбором: это единственное место,
       * где человек узнаёт, что произойдёт с чужими деньгами и чужим счётчиком.
       */}
      <div
        className={cx(
          styles["note"],
          consequence.tone === "client"
            ? undefined
            : consequence.tone === "salon"
              ? styles["noteCalm"]
              : styles["notePlain"],
        )}
        role="status"
      >
        <span>{consequence.text}</span>
      </div>

      {props.failure === null ? null : <div className={styles["failure"]}>{props.failure}</div>}

      <div className={styles["dialogButtons"]}>
        <Button variant="secondary" onClick={props.onKeep}>
          Оставить визит
        </Button>
        <Button
          variant="danger"
          disabled={!ready}
          loading={props.submitting}
          loadingLabel="Отменяем…"
          onClick={() => {
            if (initiatedBy === null || reasonCode === "") return
            props.onSubmit({ initiatedBy, reasonCode, comment, notifyClient })
          }}
        >
          Отменить визит
        </Button>
      </div>
    </JournalDialog>
  )
}

export interface NoShowSubmit {
  readonly comment: string
  readonly restrictOnlineBooking: boolean
}

/**
 * Неявка (2-2.9.3).
 *
 * Это репутационная отметка на живом человеке, поэтому здесь три вещи, которых нет
 * у обычного подтверждения: последствие числом («станет 2 неявки»), обязательная причина
 * при ограничении онлайн-записи (её прочитает следующий администратор) и напоминание
 * об обратимости в течение суток.
 */
export function NoShowDialog(props: {
  readonly appointment: AppointmentBrief
  readonly rules: BookingRulesView
  /** Право clients.blacklist.manage. Без него отметка ограничения не показывается вовсе. */
  readonly canRestrict: boolean
  readonly submitting: boolean
  readonly failure: string | null
  readonly onBack: () => void
  readonly onSubmit: (value: NoShowSubmit) => void
}): ReactNode {
  const [restrict, setRestrict] = useState(false)
  const [comment, setComment] = useState("")
  const commentId = useId()

  // Причина при ограничении обязательна и на сервере (3..1000 символов): кнопка,
  // которая уходит в отказ по длине текста, — это кнопка, выключенная не вовремя.
  const reasonMissing = restrict && comment.trim().length < 3

  return (
    <JournalDialog
      title={`Отметить, что ${props.appointment.clientName} не пришёл?`}
      onClose={props.onBack}
    >
      <div className={cx(styles["note"], styles["noteDanger"])} role="status">
        <span>
          {noShowConsequence({
            rules: props.rules,
            clientNoShowCount: props.appointment.noShowCount,
            prepaidMinor: props.appointment.prepaidMinor,
          })}
        </span>
      </div>

      {props.canRestrict ? (
        <Checkbox checked={restrict} onChange={setRestrict}>
          Ограничить клиенту онлайн-запись
        </Checkbox>
      ) : null}

      <div className={styles["field"]}>
        <label className={styles["fieldLabel"]} htmlFor={commentId}>
          Что произошло — прочитает следующий администратор
        </label>
        <textarea
          id={commentId}
          className={styles["comment"]}
          value={comment}
          placeholder={restrict ? "Обязательно при ограничении" : "Необязательно"}
          aria-invalid={reasonMissing ? true : undefined}
          onChange={(event) => {
            setComment(event.currentTarget.value)
          }}
        />
      </div>

      <div className={styles["hint"]}>
        Ошиблись? Отметку можно снять в течение суток — счётчик пересчитается.
      </div>

      {props.failure === null ? null : <div className={styles["failure"]}>{props.failure}</div>}

      <div className={styles["dialogButtons"]}>
        <Button variant="secondary" onClick={props.onBack}>
          Назад
        </Button>
        <Button
          variant="danger"
          disabled={reasonMissing}
          loading={props.submitting}
          loadingLabel="Отмечаем…"
          onClick={() => {
            props.onSubmit({ comment, restrictOnlineBooking: restrict })
          }}
        >
          Не пришёл
        </Button>
      </div>
    </JournalDialog>
  )
}

/**
 * Конфликт версий (2-2.10.3).
 *
 * ПРАВИЛО, РАДИ КОТОРОГО ЭКРАН СУЩЕСТВУЕТ: действие применяется только к той версии
 * записи, которую человек ВИДЕЛ (expectedUpdatedAt). Пока панель была открыта, соседний
 * администратор перенёс визит — и нажатие «Отменить» относилось бы уже к другой записи.
 * Поэтому сервер отвечает отказом, а кабинет показывает, ЧТО изменилось и кем, вместо
 * того чтобы молча применить действие к чужой правке.
 */
export function VersionConflictDialog(props: {
  readonly clientName: string
  readonly changedBy: string | null
  /** Что было в открытой карточке: «16:30 · Анна». */
  readonly before: string
  /** Что стало после чужой правки. null — актуальная версия ещё не доехала. */
  readonly after: string | null
  /** Подпись повторного действия: «Всё равно отменить», «Всё равно отметить». */
  readonly repeatLabel: string
  readonly repeating: boolean
  readonly onShowCurrent: () => void
  readonly onRepeat: () => void
}): ReactNode {
  return (
    <JournalDialog title="Эту запись уже изменили" onClose={props.onShowCurrent}>
      {/*
       * Формулировка безличная намеренно: сервер присылает имя строкой (changedBy),
       * а род сотрудника по имени не определить — «Ольга перенёс» в кабинете салона
       * выглядит как поломка, а не как мелочь.
       */}
      <div className={styles["dialogText"]}>
        Пока карточка была открыта, запись «{props.clientName}» изменили
        {props.changedBy === null ? "" : `: ${props.changedBy}`}. Ваше действие относилось
        к прежней версии, поэтому оно не выполнено.
      </div>

      <div className={styles["versionTile"]}>
        <div>
          <div className={styles["versionCaption"]}>Было</div>
          <div className={styles["versionValue"]}>{props.before}</div>
        </div>
        <div className={styles["versionArrow"]} aria-hidden="true">
          →
        </div>
        <div>
          <div className={styles["versionCaption"]}>Стало</div>
          <div className={styles["versionValue"]}>{props.after ?? "Загружаем…"}</div>
        </div>
      </div>

      <div className={styles["dialogButtons"]}>
        <Button variant="primary" onClick={props.onShowCurrent}>
          Показать актуальную запись
        </Button>
        <Button
          variant="danger-ghost"
          loading={props.repeating}
          loadingLabel="Повторяем…"
          onClick={props.onRepeat}
        >
          {props.repeatLabel}
        </Button>
      </div>
    </JournalDialog>
  )
}

export interface BlockTimeSubmit {
  readonly startMinute: number
  readonly endMinute: number
  readonly kind: "break" | "blocked"
  readonly note: string
}

/**
 * «Заблокировать время» (5-А3, ТЗ §2.7).
 *
 * ЧТО ЭТО ЗА ДЕЙСТВИЕ. Мастер ушёл к врачу, кресло увезли в ремонт, час нужен на уборку
 * после окрашивания — время должно перестать продаваться, и в сетке обязано быть видно,
 * ПОЧЕМУ оно закрыто. Поэтому у блока есть вид (перерыв или блокировка) и подпись:
 * «Заблокировано» без объяснения через день не помнит и тот, кто его поставил.
 *
 * ГРАНИЦЫ ПРЕДЛАГАЮТСЯ, А НЕ НАВЯЗЫВАЮТСЯ. По умолчанию — окно визита, из карточки
 * которого пришёл человек: чаще всего закрывают именно его. Изменить их можно тут же,
 * не выходя из диалога.
 */
export function BlockTimeDialog(props: {
  readonly title: string
  readonly dateLabel: string
  readonly employeeName: string
  readonly startMinute: number
  readonly endMinute: number
  readonly submitting: boolean
  readonly failure: string | null
  readonly onBack: () => void
  readonly onSubmit: (value: BlockTimeSubmit) => void
}): ReactNode {
  const [startMinute, setStartMinute] = useState(props.startMinute)
  const [endMinute, setEndMinute] = useState(props.endMinute)
  const [kind, setKind] = useState<"break" | "blocked">("break")
  const [note, setNote] = useState("")

  const wrong = endMinute <= startMinute

  return (
    <JournalDialog title={props.title} onClose={props.onBack}>
      <div className={styles["dialogSub"]}>
        {props.dateLabel} · {props.employeeName}
      </div>

      <div className={styles["field"]}>
        <span className={styles["fieldLabel"]}>Что это за время</span>
        <Chips label="Вид блокировки">
          <Chip
            active={kind === "break"}
            onClick={() => {
              setKind("break")
            }}
          >
            Перерыв
          </Chip>
          <Chip
            active={kind === "blocked"}
            onClick={() => {
              setKind("blocked")
            }}
          >
            Блокировка
          </Chip>
        </Chips>
      </div>

      <div className={styles["formRow"]}>
        <TextField
          label="С"
          type="time"
          value={formatMinute(startMinute)}
          onChange={(event) => {
            setStartMinute(minutesFromTimeInput(event.currentTarget.value, startMinute))
          }}
        />
        <TextField
          label="До"
          type="time"
          value={formatMinute(endMinute)}
          onChange={(event) => {
            setEndMinute(minutesFromTimeInput(event.currentTarget.value, endMinute))
          }}
        />
      </div>

      <TextField
        label="Подпись в сетке"
        value={note}
        placeholder="Обед, уборка кресла, ушёл к врачу"
        onChange={(event) => {
          setNote(event.currentTarget.value)
        }}
      />

      {wrong ? (
        <div className={styles["dialogNote"]}>Конец раньше начала — проверьте время.</div>
      ) : null}

      {props.failure === null ? null : <div className={styles["failure"]}>{props.failure}</div>}

      <div className={styles["dialogButtons"]}>
        <Button variant="secondary" onClick={props.onBack}>
          Назад
        </Button>
        <Button
          variant="primary"
          loading={props.submitting}
          loadingLabel="Закрываем…"
          deniedReason={wrong ? "Конец блокировки раньше её начала" : undefined}
          onClick={() => {
            props.onSubmit({ startMinute, endMinute, kind, note: note.trim() })
          }}
        >
          Заблокировать
        </Button>
      </div>
    </JournalDialog>
  )
}

/** «14:30» из поля времени в минуты от начала дня. Пустое поле оставляет прежнее. */
function minutesFromTimeInput(value: string, fallback: number): number {
  const match = /^(\d{2}):(\d{2})$/u.exec(value)
  if (match === null) return fallback
  return Number(match[1]) * 60 + Number(match[2])
}

export interface RescheduleSubmit {
  readonly startMinute: number
  readonly employeeId: string | null
  readonly initiatedBy: "client" | "staff"
  readonly notifyClient: boolean
}

/**
 * Перенос через форму (§2.8.2).
 *
 * Показывает текущее время, свободные окна и выбор мастера, а итог называет строкой
 * «Было … → Станет …» — по требованию ТЗ. Свободные окна здесь ПОДСКАЗКА, а не бронь:
 * новое время удерживается сервером в момент переноса, и если его перехватили, старая
 * запись остаётся нетронутой — об этом человек узнаёт отдельным отказом.
 */
export function RescheduleDialog(props: {
  readonly appointment: AppointmentBrief
  readonly currentEmployeeId: string | null
  readonly employees: readonly { readonly id: string; readonly displayName: string }[]
  /** Свободные начала у выбранного мастера, в минутах от начала дня. */
  readonly windows: readonly number[]
  readonly dateLabel: string
  /** Выбранный день переноса и дни на выбор: «Сегодня», «Завтра», «Дата…» (5-А3). */
  readonly day: string
  readonly dayOptions: readonly { readonly date: string; readonly label: string }[]
  readonly onDayChange: (date: string) => void
  /** День грузится: окна ещё неизвестны, и пустой список — не «мест нет». */
  readonly loadingWindows: boolean
  readonly submitting: boolean
  readonly failure: string | null
  readonly onEmployeeChange: (employeeId: string | null) => void
  readonly onBack: () => void
  readonly onSubmit: (value: RescheduleSubmit) => void
}): ReactNode {
  const [minute, setMinute] = useState<number | null>(null)
  const [initiatedBy, setInitiatedBy] = useState<"client" | "staff">("client")
  const [notifyClient, setNotifyClient] = useState(true)

  const employeeName =
    props.employees.find((item) => item.id === props.currentEmployeeId)?.displayName ??
    "Без мастера"

  return (
    <JournalDialog title={`Перенести визит ${props.appointment.clientName}`} onClose={props.onBack}>
      <div className={styles["dialogSub"]}>
        Было: {props.appointment.whenLabel} · {props.appointment.employeeName}
      </div>

      <SelectField
        label="Мастер"
        value={props.currentEmployeeId ?? ""}
        onChange={(event) => {
          const value = event.currentTarget.value
          props.onEmployeeChange(value === "" ? null : value)
          // Выбранное время принадлежало прежнему мастеру: у нового оно может быть занято.
          setMinute(null)
        }}
        options={[
          { value: "", label: "Без мастера" },
          ...props.employees.map((item) => ({ value: item.id, label: item.displayName })),
        ]}
      />

      {/*
        * ДЕНЬ ВЫБИРАЕТСЯ ЗДЕСЬ, А НЕ В ЖУРНАЛЕ. «Перенесите меня на завтра» — самая
        * частая просьба в трубку, а до этого диалог умел только сегодняшний день
        * и советовал «выберите другой день в журнале»: закрыть карточку, перелистать
        * день, найти запись заново. Половина переносов делалась мимо продукта.
        */}
      <div className={styles["field"]}>
        <span className={styles["fieldLabel"]}>День</span>
        <Chips label="День переноса">
          {props.dayOptions.map((option) => (
            <Chip
              key={option.date}
              active={option.date === props.day}
              onClick={() => {
                // Выбранное время принадлежало ПРЕЖНЕМУ дню: у нового оно может быть
                // занято или лежать вне смены, а кнопка «Перенести» оставалась живой
                // и отправляла чужую минуту.
                setMinute(null)
                props.onDayChange(option.date)
              }}
            >
              {option.label}
            </Chip>
          ))}
          <label className={styles["dayPick"]}>
            Дата…
            <input
              type="date"
              className={styles["dayInput"]}
              value={props.day}
              onChange={(event) => {
                if (event.currentTarget.value === "") return
                setMinute(null)
                props.onDayChange(event.currentTarget.value)
              }}
            />
          </label>
        </Chips>
      </div>

      <div className={styles["field"]}>
        <span className={styles["fieldLabel"]}>
          Свободное время · {props.dateLabel} <span className={styles["required"]}>*</span>
        </span>
        {props.loadingWindows ? (
          <span className={styles["hint"]}>Смотрим этот день…</span>
        ) : props.currentEmployeeId === null ? (
          <span className={styles["hint"]}>
            Выберите мастера: свободное время считается по его смене. Запись без мастера
            переносится только вместе с назначением исполнителя.
          </span>
        ) : props.windows.length === 0 ? (
          <span className={styles["hint"]}>
            У этого мастера в этот день свободного времени не осталось. Выберите другого
            мастера или другой день в журнале.
          </span>
        ) : (
          /*
           * ОКНА РАЗЛОЖЕНЫ ПО ЧАСТЯМ ДНЯ. Раньше показывались первые восемь подряд:
           * для чужого дня это 09:00…10:45, то есть первые два часа смены, — а самая
           * частая просьба звучит «перенесите на завтра на вечер». Части дня — то, как
           * о времени говорят в трубке, и в каждой видно несколько ближайших окон.
           */
          <div className={styles["windowGroups"]}>
            {groupWindows(props.windows).map((group) => (
              <div key={group.key} className={styles["windowGroup"]}>
                <span className={styles["windowGroupTitle"]}>{group.title}</span>
                <Chips label={`Свободное время · ${group.title}`}>
                  {group.minutes.map((value) => (
                    <Chip
                      key={value}
                      active={value === minute}
                      onClick={() => {
                        setMinute(value)
                      }}
                    >
                      {formatMinute(value)}
                    </Chip>
                  ))}
                </Chips>
                {group.more === 0 ? null : (
                  <span className={styles["hint"]}>
                    {moreLabel(
                      group,
                      props.windows.filter((value) =>
                        group.key === "morning"
                          ? value < 12 * 60
                          : group.key === "afternoon"
                            ? value >= 12 * 60 && value < 17 * 60
                            : value >= 17 * 60,
                      ),
                    )}
                    {" — выберите другой день или сузьте время «Датой…»"}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles["field"]}>
        <span className={styles["fieldLabel"]}>Кто переносит</span>
        <Chips label="Кто переносит">
          <Chip
            active={initiatedBy === "client"}
            onClick={() => {
              setInitiatedBy("client")
            }}
          >
            Клиент
          </Chip>
          <Chip
            active={initiatedBy === "staff"}
            onClick={() => {
              setInitiatedBy("staff")
            }}
          >
            Салон
          </Chip>
        </Chips>
      </div>

      <Switch checked={notifyClient} onChange={setNotifyClient}>
        Уведомить клиента о переносе
      </Switch>

      {minute === null ? null : (
        <div className={cx(styles["note"], styles["noteCalm"])} role="status">
          <span>
            Станет: {props.dateLabel}, {formatMinute(minute)} · {employeeName}
          </span>
        </div>
      )}

      {props.failure === null ? null : <div className={styles["failure"]}>{props.failure}</div>}

      <div className={styles["dialogButtons"]}>
        <Button variant="secondary" onClick={props.onBack}>
          Назад
        </Button>
        <Button
          variant="primary"
          disabled={minute === null}
          loading={props.submitting}
          loadingLabel="Переносим…"
          onClick={() => {
            if (minute === null) return
            props.onSubmit({
              startMinute: minute,
              employeeId: props.currentEmployeeId,
              initiatedBy,
              notifyClient,
            })
          }}
        >
          Перенести
        </Button>
      </div>
    </JournalDialog>
  )
}

/**
 * Подтверждение предупреждений (422 confirmation_required).
 *
 * Сервер согласен выполнить действие, но требует, чтобы человек прочитал последствия:
 * «у клиента три неявки», «это вне смены мастера», «до визита меньше часа». Текст пишет
 * СЕРВЕР — правила филиала знает он, и пересказывать их на клиенте значит однажды
 * разойтись с ним в цифрах. Кабинет обязан повторить запрос ровно с теми кодами,
 * которые показал человеку.
 */
export function ConfirmWarningsDialog(props: {
  readonly messages: readonly string[]
  readonly confirmLabel: string
  readonly onCancel: () => void
  readonly onConfirm: () => void
}): ReactNode {
  return (
    <JournalDialog title="Проверьте перед тем, как продолжить" onClose={props.onCancel}>
      <div className={styles["note"]} role="status">
        <span>
          {props.messages.map((message) => (
            <span key={message}>{message} </span>
          ))}
        </span>
      </div>
      <div className={styles["dialogButtons"]}>
        <Button variant="secondary" onClick={props.onCancel}>
          Отмена
        </Button>
        <Button variant="primary" onClick={props.onConfirm}>
          {props.confirmLabel}
        </Button>
      </div>
    </JournalDialog>
  )
}
